import { Box, PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IInactiveEpisodeCategory } from '../../../../common/api/employment/getInactiveEpisodeCategoryResult';
import type { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import type { IEpisodeCategory } from '../../../../common/interfaces/episodeCategory';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { SettingsCategoryFilterButtonRow } from '../../elements/SettingsCategoryFilterButtonRow';
import { PersonalSettingsInactiveCarePath } from './PersonalSettingsInactiveCarePath';

export function PersonalSettingsInactiveCarePaths() {
    const abortableFetch = useAbortableFetch();
    const [inactiveEpisodeCategories, setInactiveEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [inactiveEpisodeCategory, setInactiveEpisodeCategory] = useState<IInactiveEpisodeCategory>();
    const [selectedCategory, setSelectedCategory] = useState<IEpisodeCategory>();
    const { employmentId, episodeCategory } = useParams() as { employmentId: string; episodeCategory: EpisodeCategory };

    async function loadInactiveEpisodeCategories() {
        const url = `/api/employments/${employmentId}/inactiveEpisodeCategories`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const categories: IEpisodeCategory[] = result.json.items;

        setInactiveEpisodeCategories(categories);

        if (categories.length > 0) {
            const categoryFromUrl = categories.find((category) => category.name === episodeCategory);
            if (categoryFromUrl) {
                setSelectedCategory(categoryFromUrl);
            } else {
                setSelectedCategory(categories[0]);
            }
        }
    }

    async function loadInactiveEpisodeCategory() {
        if (selectedCategory) {
            const url = `/api/employments/${employmentId}/inactiveEpisodeCategories/${selectedCategory.name}`;

            const result = await abortableFetch.fetchJson(url, 'GET');
            setInactiveEpisodeCategory(result.json.item);
        } else {
            setInactiveEpisodeCategory(undefined);
        }
    }

    useEffect(() => {
        loadInactiveEpisodeCategories();
    }, [employmentId]);

    useEffect(() => {
        loadInactiveEpisodeCategory();
    }, [employmentId, selectedCategory]);

    if (!employmentId) {
        return null;
    }

    return (
        <>
            <PageHeader title={translateKey('episodeCategory.inactiveEpisodeCategories')} />

            <Box mb={4}>
                <SettingsCategoryFilterButtonRow
                    collection={inactiveEpisodeCategories.map((category) => ({
                        displayText: translateKey(`episodeCategoryEnum.${category.name}`),
                        ...category,
                    }))}
                    onClick={(category) => setSelectedCategory(category)}
                    selectedItem={selectedCategory}
                />
            </Box>

            {inactiveEpisodeCategory && (
                <PersonalSettingsInactiveCarePath inactiveEpisodeCategory={inactiveEpisodeCategory} />
            )}
        </>
    );
}
